<template>
  <el-card v-loading="loading">
    <template slot="header">
      <el-button style="float: right" v-if="hasAccess" type="primary" :disabled="!editForm" @click="saveTaxValue">
        Save
      </el-button>
    </template>
    <el-form label-width="250px">
      <el-form-item
        label="Tax on Rent"
      >
        <el-switch
          v-model="rentTaxFlag"
          active-color="#13ce66"
          inactive-color="#ff4949"
          active-text="Add tax in billing"
          inactive-text="No tax in billing"
          :disabled="!hasAccess"
          @change="setRentTaxFlag"
        >
        </el-switch>
      </el-form-item>
      <el-form-item
        label="Tax on Fee types"
      >
        <el-switch
          v-model="feeTaxFlag"
          active-color="#13ce66"
          inactive-color="#ff4949"
          active-text="Add fees tax in billing"
          inactive-text="No fees tax in billing"
          :disabled="!hasAccess"
          @change="setFeeTaxFlag"
        >
        </el-switch>
      </el-form-item>
      <el-form-item label="Custom Tax Label">
        <el-input
          v-model="tax.rentTaxLabel"
          style="width: 10rem"
          @input="enableSaveButton"
        >
        </el-input>
      </el-form-item>
      <el-form-item label="Sales Tax in percentage">
        <el-input-number
          v-if="tax"
          v-model.number="tax.rentTaxRate"
          :precision="2"
          :max="25"
          :min="0"
          :disabled="!hasAccess"
          @change="enableSaveButton"
        >
        </el-input-number>
      </el-form-item>
      <el-form-item :key="index" v-for="(feeTypeTaxPrice, index) in feeTypesTaxPricing" :label="`${feeTypeName(feeTypeTaxPrice.name)} Tax in percentage`">
        <el-input-number
          v-model:number="feeTypesTaxPricing[index].rate"
          :precision="2"
          :max="25"
          :min="0"
          :disabled="!hasAccess"
          @change="enableSaveButton"
        >
        </el-input-number>
      </el-form-item>
    </el-form>
  </el-card>
</template>

<script>
import axios from 'axios';
import { ORG_API, ADMIN_API_BASE } from '../env';

export default {
  name: 'FacilityTaxSettings',
  props: {
    facility: { type: String, required: true },
    facilitySecurityFee: { type: Object }
  },
  async mounted() {
    await this.fetchOrgFeeTypes();
    await this.getFacilityTax();
  },
  computed: {
    hasAccess() {
      const roles = this.$keycloak.tokenParsed.realm_access.roles;
      if (roles.includes('level_1')) {
        return true;
      }
      return false;
    },
  },
  watch: {
    async facility() {
      await this.fetchOrgFeeTypes();
      await this.getFacilityTax();
    },
  },
  data() {
    return {
      loading: true,
      rentTaxFlag: false,
      feeTaxFlag: false,
      orgFeeTypes: {},
      tax: {
        rentTaxRate: 0,
        rentTaxLabel: '',
      },
      feeTypesTaxPricing: [],
      editForm: false,
    };
  },
  methods: {
    feeTypeName(feeType) {
      if (feeType === 'Security' && this.facilitySecurityFee && this.facilitySecurityFee.label) {
        return this.facilitySecurityFee.label;
      }

      return feeType;
    },
    enableSaveButton() {
      this.editForm = true;
    },
    async fetchOrgFeeTypes() {
      const response = await axios.get(`${ORG_API}/fee/types`, {
        headers: { Authorization: `Bearer ${this.$keycloak.token}` }
      });

      if (!response.data) { return; }
      for (const feeType of response.data) {
        this.feeTypesTaxPricing = [...this.feeTypesTaxPricing, { type: feeType.code, rate: 0, name: feeType.name }];
      }
    },
    async saveTaxValue() {
      await axios.post(`${ADMIN_API_BASE}/facilities/${this.facility}/pricing/tax`, {
        tax: this.tax,
        feeTypesTaxPricing: this.feeTypesTaxPricing.map(price => ({ type: price.type, rate: price.rate })),
      }, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      this.loading = true;
      await this.getFacilityTax();
      this.loading = false;
      this.$message.success({
        title: 'Success',
        message: 'Successfully updated tax rate.',
      });
    },
    async getFacilityTax() {
      this.loading = true;
      const response = await axios.get(`${ADMIN_API_BASE}/facilities/${this.facility}`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });

      this.$root.$data.setFacilityDataAction(response.data);
      this.rentTaxFlag = response.data.rentTaxFlag;
      this.feeTaxFlag = response.data.taxOnFeeTypeEnabled;
      this.tax = response.data.tax;
      if (response.data.feeTypesTaxPricing) {
        for (const feeTypeTaxPrice of response.data.feeTypesTaxPricing) {
          const index = this.feeTypesTaxPricing.findIndex(price => price.type === feeTypeTaxPrice.type);
          if (index !== -1) {
            this.feeTypesTaxPricing[index].rate = feeTypeTaxPrice.rate;
          }
        }
      }

      if (!this.tax) {
        this.tax = {
          rentTaxRate: 0,
          rentTaxLabel: '',
        };
      }
      this.loading = false;
    },
    async setRentTaxFlag(flag) {
      try {
        await axios.post(`${ADMIN_API_BASE}/facilities/${this.facility}/pricing/tax`, {
          rentTaxFlag: flag,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.loading = true;
        await this.getFacilityTax();
        this.loading = false;
        this.$message.success({
          title: 'Success',
          message: flag ? 'Tax Enabled successfully' : 'Tax disabled successfully',
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.message ? err.message : err.resposne.data.toUpperCase(),
        });
      }
    },
    async setFeeTaxFlag(flag) {
      try {
        await axios.post(`${ADMIN_API_BASE}/facilities/${this.facility}/pricing/tax`, {
          taxOnFeeTypeEnabled: flag,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.loading = true;
        await this.getFacilityTax();
        this.loading = false;
        this.$message.success({
          title: 'Success',
          message: flag ? 'Fee Type Tax Enabled successfully' : 'Fee Type Tax disabled successfully',
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.message ? err.message : err.resposne.data.toUpperCase(),
        });
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/FacilityTaxSettings.vue